import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const TemplateSelector = ({ selectedTemplate, onSelectTemplate, onUseTemplate }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const templates = [
    {
      id: 1,
      name: 'Incident Summary Report',
      category: 'incident',
      description: 'Comprehensive template for incident investigation summaries with root cause analysis and corrective actions',
      sections: ['Executive Summary', 'Incident Details', 'Timeline', 'Root Cause Analysis', 'Corrective Actions'],
      icon: 'AlertTriangle',
      color: 'text-red-500',
      prompt: 'Generate a detailed incident summary report including timeline, root cause analysis, and corrective actions for the recent workplace incident.',
    },
    {
      id: 2,
      name: 'Audit Findings Report',
      category: 'audit',
      description: 'Structured template for safety audit findings, compliance gaps, and remediation plans',
      sections: ['Executive Summary', 'Audit Scope', 'Findings', 'Non-Conformities', 'Recommendations'],
      icon: 'ClipboardCheck',
      color: 'text-blue-500',
      prompt: 'Create an audit findings report summarizing compliance status, identified gaps, and recommended corrective actions.',
    },
    {
      id: 3,
      name: 'Management Briefing',
      category: 'briefing',
      description: 'Executive-level briefing template covering key safety metrics and strategic initiatives',
      sections: ['Executive Summary', 'Key Metrics', 'Trends', 'Strategic Initiatives', 'Budget Impact'],
      icon: 'Users',
      color: 'text-purple-500',
      prompt: 'Prepare a management briefing highlighting quarterly safety performance, key metrics, and strategic recommendations.',
    },
    {
      id: 4,
      name: 'Compliance Status Update',
      category: 'compliance',
      description: 'Regulatory compliance tracking template with permit status and upcoming requirements',
      sections: ['Compliance Overview', 'Regulatory Changes', 'Permit Status', 'Action Items', 'Timeline'],
      icon: 'Shield',
      color: 'text-green-500',
      prompt: 'Generate a compliance status update covering all regulatory requirements, permit renewals, and action items.',
    },
    {
      id: 5,
      name: 'Trend Analysis Report',
      category: 'analysis',
      description: 'Data-driven template analyzing safety trends, patterns, and predictive insights',
      sections: ['Executive Summary', 'Trend Analysis', 'Statistical Data', 'Predictive Insights', 'Recommendations'],
      icon: 'TrendingUp',
      color: 'text-orange-500',
      prompt: 'Analyze safety trends over the past 6 months, identify patterns, and provide predictive insights for improvement.',
    },
    {
      id: 6,
      name: 'Training Completion Report',
      category: 'training',
      description: 'Training program effectiveness template with completion rates and assessment results',
      sections: ['Training Overview', 'Completion Rates', 'Assessment Results', 'Competency Gaps', 'Action Plan'],
      icon: 'GraduationCap',
      color: 'text-indigo-500',
      prompt: 'Create a training completion report showing program effectiveness, competency assessment, and improvement areas.',
    },
  ];

  const categories = [
    { value: 'all', label: 'All Templates', icon: 'LayoutGrid' },
    { value: 'incident', label: 'Incident Reports', icon: 'AlertTriangle' },
    { value: 'audit', label: 'Audit Reports', icon: 'ClipboardCheck' },
    { value: 'briefing', label: 'Management Briefings', icon: 'Users' },
    { value: 'compliance', label: 'Compliance', icon: 'Shield' },
    { value: 'analysis', label: 'Analysis Reports', icon: 'TrendingUp' },
    { value: 'training', label: 'Training', icon: 'GraduationCap' },
  ];

  const filteredTemplates = templates?.filter(template => {
    const matchesSearch = template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesCategory = filterCategory === 'all' || template?.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              placeholder="Search templates..."
              className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-foreground"
            />
          </div>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
          <Icon name="Plus" size={18} />
          Create Custom Template
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {categories?.map((category) => (
          <button
            key={category?.value}
            onClick={() => setFilterCategory(category?.value)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all text-sm ${
              filterCategory === category?.value
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card text-foreground border-border hover:border-primary'
            }`}
          >
            <Icon name={category?.icon} size={16} />
            {category?.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTemplates?.map((template) => (
          <div
            key={template?.id}
            className={`bg-card border-2 rounded-lg p-6 hover:shadow-lg transition-all cursor-pointer ${
              selectedTemplate?.id === template?.id ? 'border-primary' : 'border-border'
            }`}
            onClick={() => onSelectTemplate(template)}
          >
            <div className="flex items-start justify-between mb-4">
              <div className={`w-12 h-12 rounded-lg bg-muted/50 flex items-center justify-center ${template?.color}`}>
                <Icon name={template?.icon} size={24} />
              </div>
              {selectedTemplate?.id === template?.id && (
                <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Check" size={14} color="white" />
                </div>
              )}
            </div>

            <h3 className="text-lg font-semibold text-foreground mb-2">{template?.name}</h3>
            <p className="text-sm text-muted-foreground mb-4">{template?.description}</p>

            <div className="mb-4">
              <p className="text-xs font-medium text-foreground mb-2">Included Sections:</p>
              <div className="flex flex-wrap gap-1">
                {template?.sections?.slice(0, 3)?.map((section, index) => (
                  <span key={index} className="text-xs px-2 py-1 bg-muted rounded-full text-foreground">
                    {section}
                  </span>
                ))}
                {template?.sections?.length > 3 && (
                  <span className="text-xs px-2 py-1 bg-muted rounded-full text-foreground">
                    +{template?.sections?.length - 3} more
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={(e) => {
                e?.stopPropagation();
                onUseTemplate(template);
              }}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              <Icon name="Sparkles" size={16} />
              Use Template
            </button>
          </div>
        ))}
      </div>

      {filteredTemplates?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="FileX" size={48} className="mx-auto mb-4 text-muted-foreground" />
          <p className="text-lg font-medium text-foreground mb-2">No templates found</p>
          <p className="text-sm text-muted-foreground">Try adjusting your search or filter criteria</p>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;